/**
 * CreateStoreDialog — Radix Dialog for creating a new storefront.
 *
 * Fields: Store Name, Subdomain (auto-slug, debounced availability check), Price List.
 * Plan-gated: shows upgrade prompt if at plan limit.
 */

import { useState, useEffect, useCallback, useRef } from "react"
import { Loader2, Check, X as XIcon } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { checkSubdomain, useCreateStorefront, useStoreList } from "@/hooks/useStoreList"
import { useGetTenantUsage } from "@/hooks/useGetTenantUsage"
import { captureStorefrontPublished } from "@/lib/analytics/posthog-events"

interface CreateStoreDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const DOMAIN_SUFFIX = ".shop.zaviago.com"

function slugify(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 63)
}

export function CreateStoreDialog({ open, onOpenChange }: CreateStoreDialogProps) {
  const [label, setLabel] = useState("")
  const [subdomain, setSubdomain] = useState("")
  const [subdomainManual, setSubdomainManual] = useState(false)
  const [availability, setAvailability] = useState<{ available: boolean; reason: string | null } | null>(null)
  const [checking, setChecking] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const { data: usage } = useGetTenantUsage()
  const { data: stores } = useStoreList()
  const createMutation = useCreateStorefront()

  const storefrontsIncluded = usage?.plan?.storefronts_included ?? 1
  // Use actual store list count (from CP registry) — more accurate than usage.storefront_count
  const currentCount = stores?.length ?? 0
  const atLimit = storefrontsIncluded > 0 && currentCount >= storefrontsIncluded

  // Auto-slug label -> subdomain (unless user has manually edited subdomain)
  useEffect(() => {
    if (!subdomainManual) {
      setSubdomain(slugify(label))
    }
  }, [label, subdomainManual])

  // Debounced subdomain availability check
  const checkAvailability = useCallback((value: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    setAvailability(null)

    if (!value || value.length < 3) {
      setChecking(false)
      return
    }

    setChecking(true)
    debounceRef.current = setTimeout(async () => {
      const result = await checkSubdomain(value)
      setAvailability(result)
      setChecking(false)
    }, 500)
  }, [])

  useEffect(() => {
    checkAvailability(subdomain)
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [subdomain, checkAvailability])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!label.trim() || !subdomain || !availability?.available) return

    createMutation.mutate(
      { label: label.trim(), subdomain },
      {
        onSuccess: (data) => {
          if (data.error !== "plan_limit") {
            captureStorefrontPublished()
            resetForm()
            onOpenChange(false)
          }
        },
      }
    )
  }

  const resetForm = () => {
    setLabel("")
    setSubdomain("")
    setSubdomainManual(false)
    setAvailability(null)
    setChecking(false)
  }

  const handleOpenChange = (next: boolean) => {
    if (!next) resetForm()
    onOpenChange(next)
  }

  const canSubmit = label.trim().length > 0 && subdomain.length >= 3 && availability?.available && !createMutation.isPending

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Create New Store</DialogTitle>
          <DialogDescription>
            Add another storefront for your business
          </DialogDescription>
        </DialogHeader>

        {atLimit ? (
          <div className="px-6 pb-6 pt-2 text-center space-y-4">
            <div className="w-16 h-16 mx-auto rounded-full bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center">
              <span className="text-2xl">&#9889;</span>
            </div>
            <p className="text-sm text-muted-foreground">
              Your plan allows {storefrontsIncluded} storefront{storefrontsIncluded !== 1 ? "s" : ""}.
              Upgrade to add more stores.
            </p>
            <Button variant="default" className="w-full" onClick={() => handleOpenChange(false)}>
              Upgrade Plan
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="px-6 pb-6 pt-2 space-y-4">
            {/* Store Name */}
            <div className="space-y-1.5">
              <label htmlFor="store-name" className="text-sm font-medium">
                Store Name
              </label>
              <Input
                id="store-name"
                autoFocus
                placeholder="e.g., MyBrand Outlet"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                maxLength={100}
              />
            </div>

            {/* Subdomain */}
            <div className="space-y-1.5">
              <label htmlFor="store-subdomain" className="text-sm font-medium">
                Subdomain
              </label>
              <div className="relative">
                <Input
                  id="store-subdomain"
                  placeholder="mybrand-outlet"
                  value={subdomain}
                  onChange={(e) => {
                    setSubdomainManual(true)
                    setSubdomain(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))
                  }}
                  maxLength={63}
                  className="pr-8"
                />
                {/* Availability indicator */}
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {checking && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
                  {!checking && availability?.available && (
                    <Check className="w-4 h-4 text-emerald-500" />
                  )}
                  {!checking && availability && !availability.available && (
                    <XIcon className="w-4 h-4 text-red-500" />
                  )}
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                {subdomain ? `${subdomain}${DOMAIN_SUFFIX}` : `your-store${DOMAIN_SUFFIX}`}
              </p>
              {!checking && availability && !availability.available && (
                <p className="text-xs text-red-500">{availability.reason}</p>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-2">
              <Button
                type="button"
                variant="ghost"
                className="flex-1"
                onClick={() => handleOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1"
                disabled={!canSubmit}
              >
                {createMutation.isPending && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Create Store
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
